import React, { useState } from "react";
import { useAuth } from "../../../context/authContext";
import { useNavigate } from "react-router-dom";

interface ValidationUrl {
  title: string;
  link: string;
  snippet: string;
  displayLink: string;
  formattedUrl: string;
}

interface AdditionalContentSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  messageId: string;
  loading: boolean;
  validationUrls: ValidationUrl[];
}

const AdditionalContentSidebar: React.FC<AdditionalContentSidebarProps> = ({
  isOpen,
  onClose,
  messageId,
  loading,
  validationUrls
}) => {
  const [visibleUrls, setVisibleUrls] = useState(3);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleShowMore = () => {
    setVisibleUrls((prev) => Math.min(prev + 3, validationUrls.length));
  };

  const closeSidebar = () => {
    onClose();
    setVisibleUrls(3);
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="offcanvas-backdrop fade show"
        onClick={closeSidebar}
      ></div>
      <div
        className="offcanvas offcanvas-end show w-40"
        tabIndex={-1}
        id="additionalContentSidebar"
        aria-labelledby="additionalContentSidebarLabel"
      >
        <div className="offcanvas-header">
          <h5 
            className="offcanvas-title" 
            id="additionalContentSidebarLabel"
            style={{
              fontSize: '1.25rem',
              fontWeight: 'bold',
              marginLeft: '1rem',
              padding: '10px 0px',
              alignContent: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 5

            }}
          >

            <svg 
                width="35" 
                height="50" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
            >
                  <path 
                    d="M14 2H6C4.89 2 4 2.89 4 4V20C4 21.11 4.89 22 6 22H18C19.11 22 20 21.11 20 20V8L14 2Z" 
                    stroke="#FFFFFF" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    fill="none"
                  />
                  <path 
                    d="M14 2V8H20" 
                    stroke="#FFFFFF" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    fill="none"
                  />
                  <path 
                    d="M16 13H8" 
                    stroke="#FFFFFF" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                  <path 
                    d="M16 17H8" 
                    stroke="#FFFFFF" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                  <path 
                    d="M10 9H9H8" 
                    stroke="#FFFFFF" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
            </svg>
            SUPPORTING CONTENT
          </h5>
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
            onClick={closeSidebar}
          ></button>
        </div>
        <div className="offcanvas-body scroller">
          {loading ? (
            <div className="d-flex flex-column justify-content-center align-items-center h-100">
              <div
                className="d-flex justify-content-center align-items-center spinner-border"
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : !validationUrls || validationUrls.length === 0 ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "100%" }}
            >
              <p>No additional content available at the moment.</p>
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-start align-items-center gap-2 mb-3">
                <p style={{
                    marginLeft: '1.2rem',
                    marginRight: '1.2rem',
                    fontSize: '0.875rem',
                    color: '#252525'
                }}>
                    Some additional and supporting content provided to you based on a search on the internet. Follow the links to view the content.
                </p>
              </div>
              
              <div className="mx-3">
                <div 
                  className="p-4"
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: "12px",
                    border: "1px solid #e9ecef",
                    minHeight: "60vh"
                  }}
                >
                  <div className="d-flex flex-column gap-3">
                    {validationUrls.slice(0, visibleUrls).map((url, index) => (
                      <div key={index}>
                        <a 
                          href={url.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ 
                            textDecoration: "none",
                            color: "#5376F0",
                            fontSize: "0.95rem",
                            fontWeight: "500",
                            lineHeight: "1.4"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.textDecoration = "underline";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.textDecoration = "none";
                          }}
                        >
                          {url.title}
                        </a>
                      </div>
                    ))}
                  </div>
                  
                  {visibleUrls < validationUrls.length && (
                    <div className="d-flex justify-content-center pt-3">
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleShowMore}
                        style={{
                          borderRadius: "20px",
                          padding: "6px 16px",
                          fontSize: "0.8rem"
                        }}
                      >
                        Show More ({validationUrls.length - visibleUrls} remaining)
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AdditionalContentSidebar; 